import { useMediaQuery } from "@mui/material";
import { useIntersectionObserver, useIsClient } from "@uidotdev/usehooks";
import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Color from "../../../assets/colors";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import {
  bookmarkDiscoverCompany,
  unBookmarkDiscoverCompany,
  updateSelectedCompany,
} from "../../../redux/actions/company_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../redux/actions/user_action";
import {
  ArrowLeftIconStyled,
  ArrowRightIconStyled,
  BookmarkFilledIconStyled,
  BookmarkIconStyled,
  ButtonStyled,
  CircularProgressStyled,
  CompanyLogo,
  FlexContainer,
  HScrollContainer,
  IndustryIconStyled,
  JobIconStyled,
  LinkWrapper,
  SectionContainer,
  TextStyled,
} from "../styles";
import { CardStyled, CompanyBanner, OutlineButton } from "./styles";

function DiscoverCompany(props) {
  const { companyDetail } = props || {};
  const isDesktop = useMediaQuery("(min-width: 1280px)");

  const discoverCompanies = useSelector(
    (state) => state.companies.cpDiscoverMoreCompanies
  );
  const companyList = discoverCompanies?.filter(
    (company) => company?.company?.id !== companyDetail?.id
  );

  const isClient = useIsClient();
  const [refLeft, entryLeft] = useIntersectionObserver();
  const [refRight, entryRight] = useIntersectionObserver({
    threshold: 0.925,
    root: null,
    rootMargin: "0px",
  });

  const lastFilterItem = useMemo(() => companyList.length - 1, [companyList]);

  const scrollContainerRef = useRef(null);

  function handleScroll(direction) {
    const scrollAmount = 983;
    const container = scrollContainerRef.current;

    if (container) {
      const currentScrollLeft = container.scrollLeft;
      let targetScrollLeft;

      if (direction === "left") {
        sendTrackingEvent({
          event: "CE_click-previous-companies-cdp",
        });
        targetScrollLeft = currentScrollLeft - scrollAmount;
      } else if (direction === "right") {
        sendTrackingEvent({
          event: "CE_click-next-companies-cdp",
        });
        targetScrollLeft = currentScrollLeft + scrollAmount;
      }

      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth", // Add smooth scrolling behavior
      });
    }
  }

  function handleEventViewMoreCompanies() {
    sendTrackingEvent({
      event: "CE_click-view-more-companies-cdp",
    });
  }

  return isClient ? (
    <SectionContainer aria-label="company description section">
      <TextStyled
        variant="h2"
        fontSize="1.25rem"
        fontWeight={700}
        textTransform="capitalize"
        aria-label="company description section title"
        color={"#000"}
      >
        Discover Companies You Might Like
      </TextStyled>
      <HScrollContainer
        direction="row"
        gap="0.5rem"
        marginTop="1rem"
        ref={scrollContainerRef}
      >
        {companyList.map((company, index) => {
          return (
            <CompanyCard
              key={index}
              index={index}
              company={company?.company}
              refRight={refRight}
              refLeft={refLeft}
              lastFilterItem={lastFilterItem}
              companyListLength={companyList?.length}
            />
          );
        })}
      </HScrollContainer>
      <FlexContainer
        alignItems="center"
        justifyContent="space-between"
        marginTop="1rem"
        width={"100%"}
      >
        <OutlineButton
          href="/companies"
          rel="noopener noreferrer"
          target="_blank"
          onClick={handleEventViewMoreCompanies}
          id="view-more-companies"
        >
          View More Companies
        </OutlineButton>{" "}
        {isDesktop && companyList?.length > 3 ? (
          <FlexContainer gap="1rem" sx={{ width: "auto" }}>
            <ButtonStyled
              onClick={() => handleScroll("left")}
              sx={entryLeft?.isIntersecting && { display: "none" }}
              id="prev-disc-company-list"
            >
              <ArrowLeftIconStyled />
            </ButtonStyled>

            <ButtonStyled
              onClick={() => handleScroll("right")}
              disabled={entryRight?.isIntersecting}
              id="next-disc-company-list"
            >
              <ArrowRightIconStyled />
            </ButtonStyled>
          </FlexContainer>
        ) : null}
      </FlexContainer>
    </SectionContainer>
  ) : null;
}

function CompanyCard(props) {
  const {
    index,
    company,
    companyListLength,
    refLeft,
    refRight,
    lastFilterItem,
  } = props;

  const dispatch = useDispatch();
  const [targetCompany, setTargetCompany] = useState(null);

  const bookmarking = useSelector(
    (state) => state.companies.bookmarkingDiscoverCompany
  );
  const unbookmarking = useSelector(
    (state) => state.companies.unbookmarkDiscoverCompany
  );

  async function handleBookmarkCompany(e, companyId) {
    e.preventDefault();
    e.stopPropagation();

    const loggedIn = checkAuthentication();

    if (!loggedIn) {
      return dispatch(updateSelectedCompany(company)).then(() => {
        dispatch(updateSignUpOrigin("save_company")).then(() => {
          dispatch(updateShowSignInModalStatus(true));
        });
      });
    }

    setTargetCompany(companyId);

    if (!company?.bookmark) {
      dispatch(bookmarkDiscoverCompany({ companyId: companyId }));

      sendTrackingEvent({
        event: "CE_follow-company-card-cdp",
        "company-id": companyId,
      });

      toast.success("Company bookmarked");
      setTargetCompany(null);
    } else if (company?.bookmark) {
      dispatch(unBookmarkDiscoverCompany({ companyId: companyId }));

      sendTrackingEvent({
        event: "CE_unfollow-company-card-cdp",
        "company-id": companyId,
      });

      toast.success("Company unbookmarked");
      setTargetCompany(null);
    }
  }

  function handleEventCompanyCard(company) {
    sendTrackingEvent({
      event: "CE_click-company-cdp",
      "company-id": company?.id,
    });
  }

  return (
    <LinkWrapper
      href={"/companies/" + company?.slug}
      rel="noopener noreferrer"
      target="_blank"
      onClick={() => handleEventCompanyCard(company)}
    >
      <CardStyled
        sx={{ width: companyListLength > 3 ? "20rem" : "100%" }}
        ref={index === 0 ? refLeft : index === lastFilterItem ? refRight : null}
      >
        <CompanyBanner
          style={{
            backgroundImage: `url(${company?.coverImageThumb})`,
          }}
        >
          <FlexContainer
            flexDirection={"column"}
            alignItems="flex-end"
            justifyContent="space-between"
            height="100%"
            padding="0.75rem"
          >
            {(bookmarking || unbookmarking) && targetCompany === company?.id ? (
              <CircularProgressStyled />
            ) : company?.bookmark ? (
              <BookmarkFilledIconStyled
                onClick={(e) => handleBookmarkCompany(e, company?.id)}
                width="2rem"
                height="2rem"
                stroke={Color.black}
                id="disc-company-bookmark"
              />
            ) : (
              <BookmarkIconStyled
                onClick={(e) => handleBookmarkCompany(e, company?.id)}
                width="2rem"
                height="2rem"
                stroke={Color.black}
                id="disc-company-bookmark"
              />
            )}

            <CompanyLogo
              src={company?.logo}
              loading="lazy"
              width={80}
              height={80}
              alt={company?.name + " company logo"}
              aria-label="company-logo-image"
              id="disc-company-logo-big"
              style={{ border: `1px solid ${Color.black}` }}
            />
          </FlexContainer>
        </CompanyBanner>
        <FlexContainer
          flexDirection={"column"}
          padding="0.75rem 1rem 1rem 1rem"
          gap="0.5rem"
        >
          <FlexContainer gap={"1rem"}>
            <CompanyLogo
              src={company?.logo}
              loading="lazy"
              width={24}
              height={24}
              alt={company?.name + " company logo"}
              aria-label="company-logo-image"
              id="disc-company-logo-small"
              style={{
                border: `0.5px solid ${Color.black}`,
                borderRadius: "0.25rem",
              }}
            />

            <TextStyled
              variant="h3"
              fontSize="1.125rem"
              fontWeight={700}
              fontFamily={"Karla"}
              letterSpacing={"0.27px"}
              whiteSpace={"nowrap"}
              overflow={"hidden"}
              textOverflow={"ellipsis"}
              id="disc-company-name"
            >
              {company?.name}
            </TextStyled>
          </FlexContainer>
          <FlexContainer flexDirection={"column"} gap="0.5rem">
            <FlexContainer
              alignItems="center"
              gap={"0.5rem"}
              textTransform="capitalize"
              id="disc-company-industry"
            >
              <IndustryIconStyled />

              <TextStyled
                variant="h4"
                fontSize="0.875rem"
                fontWeight={400}
                fontFamily={"Karla"}
                letterSpacing={"0.15px"}
              >
                {company?.industry}
              </TextStyled>
            </FlexContainer>

            <FlexContainer
              alignItems="center"
              gap={"0.5rem"}
              id="disc-company-job-count"
            >
              <JobIconStyled />
              <TextStyled
                variant="h4"
                fontSize="0.875rem"
                fontWeight={400}
                fontFamily={"Karla"}
                letterSpacing={"0.15px"}
              >
                {company?.jobCount + " jobs"}
              </TextStyled>
            </FlexContainer>
          </FlexContainer>
        </FlexContainer>
      </CardStyled>
    </LinkWrapper>
  );
}

export default DiscoverCompany;
